import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { 
	BrowserRouter as Router, 
	Route,
	Routes,
	NavLink,
	Navigate,
} from 'react-router-dom'
import RouteConstants from '../routes'
import pageComponents from './pageComponents'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import Link from '@mui/material/Link';
import CssBaseline from '@mui/material/CssBaseline';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import useStyles from '../styles/Styles';

import UmasAppBar from './UmasAppBar';
import BottomAppBar from './BottomAppBar';
import EmbeddedAppBar from './EmbeddedAppBar';
import Footer from './Footer';
import TimeoutMonitor from './TimeoutMonitor';

import { initRequest } from '../actions';
import { setPayPalSdkLoaded, setPayPalSdkError } from '../actions'; 

import _ from 'lodash';
import { isTrue } from '../utils';

const Root = () => {
	const classes = useStyles();
	
	const umas4m_config = window.$umas4m_config;
	
	const [drawerOpen, setDrawerOpen] = useState(false);
	
	const generalSettings = useSelector(state => state.settings.general);
	const user = useSelector(state => state.user);
	const dispatch = useDispatch();
	
	const payPalSettings = useSelector(state => state.settings.invoices.data.paypal);
	const sdkLoaded = useSelector(state => state.payPalSdkLoaded);
	
	const u4mRoutes = useSelector(state => state.settings.routes); 
	const filteredLinks = useSelector(state => state.settings.links);
	const courseLabels = useSelector(state => state.settings.labels_courses.data)
	
	const logo = useSelector(state => state.requests.logo);

	const [links, setLinks] = useState([]);
	useEffect(() => {
		if(user && user.member_no > 0){
			setLinks(_.chain(RouteConstants).pick(filteredLinks).pickBy({loggedin: true}).value())
		} else {
			setLinks(_.chain(RouteConstants).pick(filteredLinks).pickBy({loggedout: true}).value())
		}
	}, [user, filteredLinks]);
	
	useEffect(() => {
		dispatch(initRequest());
	}, [dispatch])
	
	const loadPayPalSdk = () => {

		const src="https://www.paypal.com/sdk/js?client-id="+payPalSettings.paypal_client_id+"&currency=AUD&locale=en_AU";
		const script = document.createElement('script');
		script.src = src;
		script.type = 'text/javascript';
		script.async = true;
		script.onload = () => {
			dispatch(setPayPalSdkLoaded(true));
		};
		script.onerror = () => {
			console.log('Failed to load PayPal script');
			dispatch(setPayPalSdkError(true));
		};
		
		document.body.appendChild(script);
	}
	
	// loadPayPalSdk() doesn't need to be a dependency
	useEffect(() => {
		if(!sdkLoaded && isTrue(payPalSettings.accept) && !_.isEmpty(payPalSettings.paypal_client_id)){ 
			loadPayPalSdk();
		} // eslint-disable-next-line
	}, [sdkLoaded, payPalSettings])

	const handleDrawerToggle = () => event => {
		if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
			return;
		}
		setDrawerOpen(!drawerOpen);
	};
	
	const drawer = (mobile, embedded) => (
		<div >
			{embedded ? 
			<div className={classes.embeddedDrawerTop}>
				<Typography variant="h6" noWrap>U-MAS for Members</Typography>
			</div> :
			<div className={classes.toolbar} >
				<Toolbar>
					<Typography variant="h6" noWrap>U-MAS</Typography>
				</Toolbar>
			</div>
			}
			{embedded ? null : <Divider />}
			<div className={embedded ? classes.embeddedDrawerMiddle : null}>
			<List className={classes.nav} >

				{_.map(links, (route, i) => {
					return route.hidden ? null : (
						<ListItem button 
							key={i} 
							onClick={(mobile || embedded) ? handleDrawerToggle() : null}
							component={NavLink} 
							to={RouteConstants[i].path} 
						>
						{RouteConstants[i].icon ? 
						<ListItemIcon className={classes.navicon}>
							{RouteConstants[i].icon}
						</ListItemIcon>
						: null}
						<ListItemText inset={RouteConstants[i].icon ? false : true}
							primary={i === 'courses' ? courseLabels.coursesCapLabel : RouteConstants[i].title} 
							className={classes.naviconText} />
						</ListItem>
				)})}
				{embedded ? null :
				<>
				<Divider />
				<ListItem button 
					component={Link}
					href={generalSettings.data.website}
					target='_blank'
				>
				<ListItemText 
					primary={<Typography variant='caption' color='textSecondary'>{generalSettings.data.website_link_text}</Typography>} 
					secondary={<img src={logo.image} 
						className={classes.u3aLogo} 
						alt={generalSettings.data.display_name}
					/>}
				/>
				</ListItem>
				</>
				}
			</List>
			</div>
			{embedded ?
			<div className={classes.embeddedDrawerBottom}>
			<Button
				color='primary'
				onClick={handleDrawerToggle()}
			>Close</Button>
			</div>
			: null }
		</div>
	);

	return (
		<Router basename={umas4m_config.router_basename}>
		
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<div className={classes.root}>
				{umas4m_config.embedded ?
				<>
				<ScopedCssBaseline/>
				<nav className={classes.embeddedDrawer} aria-label="navigation menu">
				<Drawer
				variant="temporary"
				anchor='bottom'
				open={drawerOpen}
				onClose={handleDrawerToggle()}
				classes={{
				  paper: classes.embeddedDrawerPaper,
				}}
				ModalProps={{
				  keepMounted: true, // Better open performance on mobile.
				}}
				>
				{drawer(false, true)}
				</Drawer>
				</nav>
				</> :
				<>
				<CssBaseline />
				<UmasAppBar onClick={handleDrawerToggle()} />
				<nav className={classes.drawer} aria-label="navigation menu">
					<Drawer
						sx={{ display: {xs: 'block', sm: 'none'}}}
						variant="temporary"
						anchor='left'
						open={drawerOpen}
						onClose={handleDrawerToggle()}
						classes={{
						  paper: classes.drawerPaper,
						}}
						ModalProps={{
						  keepMounted: true, // Better open performance on mobile.
						}}
						>
						{drawer(true, false)}
					</Drawer>
					<Drawer
						sx={{ display: {xs: 'none', sm: 'block'}}}
						classes={{
						  paper: classes.drawerPaper,
						}}
						variant="permanent"
						open
						>
						{drawer(false, false)}
					</Drawer>
				</nav>
				</>
				}
				
				<Routes>
				{_.map(u4mRoutes, (route, i) => {
					return (
					<Route 
						path={RouteConstants[i].path} 
						key={i}
						element={(user.member_no === 0 && RouteConstants[i].loggedout) || (user.member_no > 0 && RouteConstants[i].loggedin) ? (
						<main className={umas4m_config.embedded ? classes.embeddedContent : classes.content}>
							{umas4m_config.embedded ? null :
							<div className={classes.toolbar} />
							}
							{pageComponents[i]}
							<Footer 
								adminLogin={_.has(u4mRoutes, 'login_as') && user.member_no === 0 ? RouteConstants.login_as.path : '' } 
							/>
						</main>
						) : (
						<Navigate to={{ pathname: RouteConstants[i].redirect, }} />
						)}
					/>
				)})}
				<Route 
					path="*" 
					element={<Navigate to={{ pathname: "/", }} />}
				/>
				</Routes>
				{umas4m_config.embedded ?
				<EmbeddedAppBar onClick={handleDrawerToggle()} /> :
				<BottomAppBar/> 
				}
				<TimeoutMonitor/>
			</div>
		</LocalizationProvider>
		</Router>
	);
}

Root.propTypes = {
  //store: PropTypes.object.isRequired
}

export default Root